/**
 * Campuses API — backend calls for the cross-campus Campuses page.
 *
 * Read:   getcampuses              (all campuses)
 *         core_corporate fetchall  (corporations dropdown)
 *         att_timezones fetchall   (timezones dropdown)
 *         att_user_roles fetchall  (impersonate role dropdown)
 * Write:  campus_add               (add new / migrate campus)
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all campuses across all corporations.
 *
 * V1 procedure: getcampuses
 * The empty campus_filter returns all campuses.
 */
export function fetchCampuses(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getcampuses',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { campus_filter: '' },
    readOnly,
  });
}

/**
 * Fetch all corporations (for the Add Campus drawer dropdown).
 *
 * V1: Tbl: "core_corporate", procedure: "fetchall", reqType: "direct"
 */
export function fetchCorporations(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'core_corporate',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all timezones (for the Add Campus drawer dropdown).
 *
 * V1: Tbl: "att_timezones", procedure: "fetchall", reqType: "direct"
 */
export function fetchTimezones(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_timezones',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all user roles (for the Impersonate role dropdown).
 *
 * V1: Tbl: "att_user_roles", procedure: "fetchall", reqType: "direct"
 */
export function fetchUserRoles(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_user_roles',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new campus or migrate a campus from 2.0.
 *
 * V1 TransType: campus_add
 *
 * @param {object} campusData — see payload shape in plan
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addCampus(campusData, session) {
  return apiTransaction({
    transType: 'campus_add',
    payload: [campusData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
